import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { AVATARS, setPersonalAppearance, setSharedAppearance, usePersonalAppearance } from '../lib/appearance.js';
import '../styles/appearance.css';

/** The curated assistant artwork. Artwork only: it never changes the assistant's role, voice or access. */
export function AppearancePicker({ value, onChange, disabled = false, label = 'Jabberr appearance' }: { value: string; onChange: (id: string) => void; disabled?: boolean; label?: string }) {
  return (
    <div className="appearance-picker" role="group" aria-label={label}>
      {AVATARS.map((avatar) => (
        <button key={avatar.id} type="button" disabled={disabled} aria-pressed={value === avatar.id} onClick={() => onChange(avatar.id)}>
          <img src={avatar.imageUrl} alt="" />
          <span>{avatar.label}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * The signed-in person's own choice, shown only when the server allows it (the Tarawa team). Shared
 * meetings show the choice made for that meeting instead.
 */
export function PersonalAppearance({ userId }: { userId: string }) {
  const selected = usePersonalAppearance();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [available, setAvailable] = useState(false);
  const generation = useRef(0);
  useEffect(() => {
    const current = ++generation.current;
    setBusy(false); setError(''); setOpen(false); setAvailable(false);
    setPersonalAppearance('professional');
    void api.appearance.mine().then((result) => {
      if (generation.current !== current) return;
      setPersonalAppearance(result.jabberrAssistantImageId ?? result.jabberrAvatarId);
      setAvailable(true);
    }).catch(() => undefined);
    return () => { generation.current++; setPersonalAppearance('professional'); };
  }, [userId]);
  if (!available) return null;
  const choose = (id: string) => {
    const current = generation.current;
    setBusy(true); setError('');
    void api.appearance.setMine(id)
      .then((result) => { if (current === generation.current) setPersonalAppearance(result.jabberrAssistantImageId ?? result.jabberrAvatarId); })
      .catch(() => { if (current === generation.current) setError('Appearance could not be saved. Try again.'); })
      .finally(() => { if (current === generation.current) setBusy(false); });
  };
  return (
    <div className="appearance-personal">
      <button type="button" className="side-link small" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <img className="appearance-chip" src={AVATARS.find((a) => a.id === selected)?.imageUrl ?? AVATARS[0].imageUrl} alt="" />
        Jabberr appearance
      </button>
      {open ? (
        <section className="appearance-panel" aria-label="Personal Jabberr appearance">
          <p className="muted small">Your choice for your own conversations. Shared meetings use the choice made for the meeting.</p>
          <AppearancePicker value={selected} disabled={busy} onChange={choose} />
          {error ? <p role="alert" className="small">{error}</p> : null}
        </section>
      ) : null}
    </div>
  );
}

export interface MeetingAppearanceState {
  meetingId: string;
  avatarId: string;
  busy: boolean;
  error: string;
  choose: (id: string) => void;
}

/**
 * A meeting's shared artwork. Starts from the meeting record, rechecks every 20 seconds so a change by
 * the starter reaches everyone in the room, and sets the shared appearance for the page while mounted.
 */
export function useMeetingAppearance(meetingId: string, initial: string | undefined): MeetingAppearanceState {
  const [avatarId, setAvatarId] = useState(initial ?? 'professional');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef(0);
  useEffect(() => { if (initial) setAvatarId(initial); }, [initial]);
  useEffect(() => {
    if (!meetingId) return;
    const current = ++generation.current;
    const refresh = () => {
      void api.meetings.appearance(meetingId)
        .then((result) => { if (generation.current === current) setAvatarId(result.jabberrAssistantImageId ?? result.jabberrAvatarId); })
        .catch(() => undefined); // Meeting access is enforced by the meeting routes themselves.
    };
    const timer = window.setInterval(refresh, 20_000);
    return () => { generation.current++; window.clearInterval(timer); };
  }, [meetingId]);
  useEffect(() => { setSharedAppearance(avatarId); return () => setSharedAppearance(null); }, [avatarId]);
  const choose = (id: string) => {
    const current = generation.current;
    setBusy(true); setError('');
    void api.meetings.setAppearance(meetingId, id)
      .then((result) => { if (current === generation.current) setAvatarId(result.jabberrAssistantImageId ?? result.jabberrAvatarId); })
      .catch(() => { if (current === generation.current) setError('Appearance could not be saved. Try again.'); })
      .finally(() => { if (current === generation.current) setBusy(false); });
  };
  return { meetingId, avatarId, busy, error, choose };
}

/** The starter's or an owner's chooser for the meeting's shared artwork. */
export function MeetingAppearance({ appearance, inline = false }: { appearance: MeetingAppearanceState; inline?: boolean }) {
  return (
    <details className={inline ? 'room-appearance room-appearance-inline' : 'room-appearance'}>
      <summary className="btn btn-soft">Jabberr appearance</summary>
      <div className="room-appearance-panel">
        <p className="muted small">Everyone in this meeting sees this choice.</p>
        <AppearancePicker label="Shared Jabberr appearance" value={appearance.avatarId} disabled={appearance.busy} onChange={appearance.choose} />
        {appearance.error ? <p role="alert" className="small">{appearance.error}</p> : null}
      </div>
    </details>
  );
}
